import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { FEATURES } from "@/data/content";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/integrations", label: "Integrations" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];
const RESOURCES = [
  { to: "/resources#guides", label: "Marketing Guides" },
  { to: "/resources#tips", label: "AI Marketing Tips" },
  { to: "/resources#faqs", label: "FAQs" },
];

const MenuDrop = ({ label, items, testId }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger data-testid={testId} className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium text-foreground/80 hover:text-foreground outline-none">
      {label} <ChevronDown className="h-3.5 w-3.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-60 rounded-xl p-2">
      {items.map((i) => (
        <DropdownMenuItem key={i.to} asChild>
          <Link to={i.to} data-testid={`${testId}-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="cursor-pointer rounded-lg">{i.label}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const platformItems = [{ to: "/platform", label: "AI Marketing Agents" }, ...FEATURES.map((f) => ({ to: `/platform#${f.slug}`, label: f.title }))];
  return (
    <header className="sticky top-0 z-50 glass border-x-0 border-t-0">
      <div className="container-x !max-w-[1440px] flex h-[72px] items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-link" className="text-foreground"><Logo className="h-8 sm:h-9" /></Link>
        <nav className="hidden xl:flex items-center gap-1">
          <NavLink to="/" end data-testid="nav-home" className="whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium text-foreground/80 hover:text-foreground">Home</NavLink>
          <MenuDrop label="Platform" items={platformItems} testId="nav-platform" />
          {LINKS.slice(0, 3).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={({ isActive }) => `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium ${isActive ? "text-[#7c3aed]" : "text-foreground/80 hover:text-foreground"}`}>{l.label}</NavLink>
          ))}
          <MenuDrop label="Resources" items={RESOURCES} testId="nav-resources" />
          {LINKS.slice(3).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={({ isActive }) => `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium ${isActive ? "text-[#7c3aed]" : "text-foreground/80 hover:text-foreground"}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <button data-testid="nav-dashboard-btn" onClick={() => nav(user.role === "admin" ? "/admin" : "/app")} className="btn-brand !py-2.5 hidden sm:inline-flex whitespace-nowrap">Open workspace</button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="hidden md:inline-flex whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold hover:bg-foreground/5">Log In</Link>
              <Link to="/tour" data-testid="nav-demo-link" className="hidden 2xl:inline-flex btn-ghost !py-2.5 whitespace-nowrap">Explore Demo</Link>
              <Link to="/register" data-testid="nav-get-started-btn" className="btn-brand !py-2.5 hidden sm:inline-flex whitespace-nowrap">Get Started</Link>
            </>
          )}
          <button data-testid="nav-mobile-toggle" className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10" onClick={() => setOpen(!open)}>
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t bg-background px-5 py-5 space-y-1" data-testid="nav-mobile-menu">
          {[{ to: "/", label: "Home" }, { to: "/platform", label: "Platform" }, ...LINKS, { to: "/resources", label: "Resources" }, { to: "/tour", label: "Explore Demo" }, { to: user ? "/app" : "/login", label: user ? "Open workspace" : "Log In" }].map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-foreground/5">{l.label}</Link>
          ))}
          {!user && <Link to="/register" onClick={() => setOpen(false)} className="btn-brand w-full mt-3">Get Started</Link>}
        </div>
      )}
    </header>
  );
};
